
import { useState, useMemo } from "react"
import { PiSortAscendingLight, PiSortDescendingLight } from "react-icons/pi"
import { RiDeleteBin5Line } from "react-icons/ri"
import { BiSolidEdit } from "react-icons/bi"
import { FaCopy, FaFileCsv, FaFileExcel, FaFilePdf } from "react-icons/fa"
import { jsPDF } from "jspdf"
import * as XLSX from "xlsx"
import { saveAs } from "file-saver"
import { Circles } from 'react-loader-spinner';

import { FadeLoader } from 'react-spinners';


// Static data
const initialData = [
  { id: 1, name: "John Doe", email: "john@example.com", phone: "1234567890" },
  { id: 2, name: "Jane Smith", email: "jane@example.com", phone: "0987654321" },
  { id: 3, name: "Bob Johnson", email: "bob@example.com", phone: "1122334455" },
  { id: 4, name: "Alice Brown", email: "alice@example.com", phone: "5566778899" },
  { id: 5, name: "Charlie Davis", email: "charlie@example.com", phone: "2233445566" },
]

function Register() {
  const [tableData, setTableData] = useState(initialData)
  console.log(tableData)
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" })
  const [searchQuery, setSearchQuery] = useState("")
  const [entriesPerPage, setEntriesPerPage] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)

  const handleSort = (column) => {
    let direction = "asc"
    if (sortConfig.key === column && sortConfig.direction === "asc") {
      direction = "desc"
    }
    setSortConfig({ key: column, direction })
  }

  const getSortIcon = (column) => {
    if (sortConfig.key !== column) {
      return <PiSortAscendingLight className="text-gray-500" />
    }
    return sortConfig.direction === "asc" ? <PiSortAscendingLight /> : <PiSortDescendingLight />
  }

  const handleSearch = (e) => setSearchQuery(e.target.value)

  const handleEntriesChange = (e) => {
    const value = e.target.value
    setEntriesPerPage(value === "All" ? tableData.length : Number(value))
    setCurrentPage(1)
  }

  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1))
  const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages))
  const handlePageChange = (page) => setCurrentPage(page)

  const handleEdit = (id) => {
    console.log("Edit customer with id:", id)
  }

  const handleDelete = (id) => {
    setTableData(tableData.filter((row) => row.id !== id))
  }

  const handleCopy = () => {
    // Collect the data from the table
    const tableContent = tableData
      .map((row) => {
        return [
          row.id,
          row.name.split(" ")[0],
          row.name.split(" ")[1] || "",
          row.email,
          row.phone,
        ];
      })
      .join("\n");  // Join each row with a newline for better readability
  
    // Check if Clipboard API is supported
    if (navigator?.clipboard) {
      // Use Clipboard API if available
      navigator.clipboard.writeText(tableContent)
        .then(() => {
          alert("Table data copied to clipboard!");
        })
        .catch((error) => {
          console.error("Error copying to clipboard: ", error);
          alert("Failed to copy data. Please try again.");
        });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = tableContent;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        alert("Table data copied to clipboard!");
      } catch (err) {
        console.error("Error using execCommand: ", err);
        alert("Failed to copy data. Please try again.");
      }
      document.body.removeChild(textArea);  // Remove the textarea after copying
    }
  };

  const handleCsvExport = () => {
    console.log("CSV export")
    const csvData = tableData.map((row) => Object.values(row).join(","))
    const csvContent = `data:text/csv;charset=utf-8,${csvData.join("\n")}`
    const encodedUri = encodeURI(csvContent)
    const link = document.createElement("a")
    link.setAttribute("href", encodedUri)
    link.setAttribute("download", "tableData.csv")
    link.click()
  }

  const handleExcelExport = () => {
    const ws = XLSX.utils.json_to_sheet(tableData)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, "Sheet1")
        const excelFile = XLSX.write(wb, { bookType: "xlsx", type: "array" })
        saveAs(new Blob([excelFile], { type: "application/octet-stream" }), "tableData.xlsx")
    console.log("Excel export")
  }

  const handlePdfExport = () => {
       const doc = new jsPDF()
        doc.setFontSize(16)
        doc.text("Customer Table Data", 10, 10)
        const tableHeaders = ["ID", "First Name", "Last Name", "Email", "Phone"]
        const tableRows = tableData.map((row) => [
          row.id,
          row.name.split(" ")[0],
          row.name.split(" ")[1] || "",
          row.email,
          row.phone,
          ])
          doc.autoTable({
            head: [tableHeaders],
            body: tableRows,
            startY: 20,
            theme: "striped",
          })
          doc.save("CustomerData.pdf")
    console.log("PDF export")
  }

  const filteredData = useMemo(() => {
    return tableData.filter(
      (row) =>
        row.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        row.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        row.phone.includes(searchQuery),
    )
  }, [tableData, searchQuery])

  const sortedData = useMemo(() => {
    if (sortConfig.key) {
      return [...filteredData].sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === "asc" ? -1 : 1
        if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === "asc" ? 1 : -1
        return 0
      })
    }
    return filteredData
  }, [filteredData, sortConfig])

  const totalPages = Math.ceil(sortedData.length / entriesPerPage)
  const startIndex = (currentPage - 1) * entriesPerPage
  const paginatedData = sortedData.slice(startIndex, startIndex + entriesPerPage)

  return (
    <div className="container mx-auto px-4 md:px-6 lg:px-8  xl:px-24 py-6 ">
      <div className="text-2xl font-bold p-4">
        <span>Guest Customers</span>
      </div>

      <div className="flex flex-col gap-4 mb-4">
        <input
          type="text"
          placeholder="Search customer..."
          value={searchQuery}
          onChange={handleSearch}
          className="p-2 w-full rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
        />

        <div className="flex flex-wrap justify-center gap-2">
          <button
            onClick={handleCopy}
            className="bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold py-2 px-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
          >
            <FaCopy className="text-xl" />
            <span className="text-sm">Copy</span>
          </button>
          <button
            onClick={handleCsvExport}
            className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-semibold py-2 px-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
          >
            <FaFileCsv className="text-xl" />
            <span className="text-sm">CSV</span>
          </button>
          <button
            onClick={handleExcelExport}
            className="bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold py-2 px-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
          >
            <FaFileExcel className="text-xl" />
            <span className="text-sm">Excel</span>
          </button>
          <button
            onClick={handlePdfExport}
            className="bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold py-2 px-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
          >
            <FaFilePdf className="text-xl" />
            <span className="text-sm">PDF</span>
          </button>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm">Show</span>
            <select
              value={entriesPerPage}
              onChange={handleEntriesChange}
              className="p-1 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value="All">All</option>
            </select>
            <span className="text-sm">entries</span>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        {/* Table view for larger screens */}
        <div className="hidden md:block">
          <table className="min-w-full table-auto bg-white border border-gray-300 shadow-md rounded-lg">
            <thead className="bg-blue-100 text-left">
              <tr>
                <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort("name")}>
                  <div className="flex items-center gap-2">Customer Name {getSortIcon("name")}</div>
                </th>
                <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort("email")}>
                  <div className="flex items-center gap-2">Email Address {getSortIcon("email")}</div>
                </th>
                <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort("phone")}>
                  <div className="flex items-center gap-2">Phone {getSortIcon("phone")}</div>
                </th>
                <th className="px-4 py-2 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 border-b">{row.name}</td>
                  <td className="px-4 py-2 border-b">{row.email}</td>
                  <td className="px-4 py-2 border-b">{row.phone}</td>
                  <td className="px-4 py-2 border-b">
                    <button onClick={() => handleEdit(row.id)} className="text-green-700 hover:text-green-900 mr-2">
                      <BiSolidEdit className="text-2xl" />
                    </button>
                    <button onClick={() => handleDelete(row.id)} className="text-red-500 hover:text-red-600">
                      <RiDeleteBin5Line className="text-2xl" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Card view for mobile */}
        <div className="grid grid-cols-1 gap-4 md:hidden">
          {paginatedData.map((row) => (
            <div key={row.id} className="bg-white p-3 rounded-lg shadow-md border border-gray-200">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-bold text-lg">{row.name}</h3>
                <div className="flex space-x-2">
                  <button onClick={() => handleEdit(row.id)} className="text-green-700 hover:text-green-900">
                    <BiSolidEdit className="text-xl" />
                  </button>
                  <button onClick={() => handleDelete(row.id)} className="text-red-500 hover:text-red-600">
                    <RiDeleteBin5Line className="text-xl" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1 text-sm">
                <p className="text-gray-600">Email:</p>
                <p className="font-medium">{row.email}</p>
                <p className="text-gray-600">Phone:</p>
                <p className="font-medium">{row.phone}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col items-center gap-4 mt-4">
        <div className="text-sm text-gray-600 text-center">
          Showing {startIndex + 1} to {Math.min(startIndex + paginatedData.length, sortedData.length)} of{" "}
          {sortedData.length} entries
        </div>

        {entriesPerPage !== "All" && (
          <div className="flex items-center">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="px-2 py-1 text-sm bg-gray-200 rounded-l hover:bg-gray-300 disabled:opacity-50"
            >
              Prev
            </button>
            {[...Array(totalPages).keys()].map((page) => (
              <button
                key={page + 1}
                onClick={() => handlePageChange(page + 1)}
                className={`px-2 py-1 text-sm border ${
                  currentPage === page + 1 ? "bg-blue-500 text-white" : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                {page + 1}
              </button>
            ))}
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="px-2 py-1 text-sm bg-gray-200 rounded-r hover:bg-gray-300 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default Register

// import { RiDeleteBin5Line } from "react-icons/ri";
// import { BiSolidEdit } from 'react-icons/bi';
// import { FaCopy, FaFileCsv, FaFileExcel, FaFilePdf } from 'react-icons/fa';
// import { PiSortAscendingLight } from "react-icons/pi";
// import { PiSortDescendingLight } from "react-icons/pi";
// function Guest() {
//   const [formData, setFormData] = useState({
//     id: null,
//     name: "",
//     email: "",
//     mobile: "",
//   });

//   const [searchQuery, setSearchQuery] = useState("");
//   const [tableData, setTableData] = useState([
//     { id: 1, name: "Maitri1", email: "abc1@example.com", mobile: "9876543210" },
//     { id: 2, name: "Man2", email: "abc12@example.com", mobile: "1234567890" },
//     { id: 3, name: "Man", email: "abc12@example.com", mobile: "1234567890" },
//     { id: 4, name: "Man", email: "abc12@example.com", mobile: "1234567890" },
//     { id: 5, name: "Man", email: "abc12@example.com", mobile: "1234567890" },
//     { id: 6, name: "Hem", email: "abc123@example.com", mobile: "9123456780" },
//     { id: 7, name: "Hem", email: "abc123@example.com", mobile: "9123456780" },
//     { id: 8, name: "Abc", email: "abc123@example.com", mobile: "9123456780" },
//     { id: 9, name: "Hem", email: "abc123@example.com", mobile: "9123456780" },
//     { id: 10, name: "Hem", email: "abc123@example.com", mobile: "9123456780" },
//     { id: 11, name: "Maitri1", email: "abc1@example.com", mobile: "9876543210" },
//     { id: 12, name: "Man2", email: "abc12@example.com", mobile: "1234567890" },
//     { id: 13, name: "zxy", email: "abc12@example.com", mobile: "1234567890" },
//     { id: 14, name: "Man", email: "abc12@example.com", mobile: "1234567890" },
//     { id: 15, name: "xyz", email: "abc12@example.com", mobile: "1234567890" },
//     { id: 16, name: "Hem", email: "abc123@example.com", mobile: "9123456780" },
//     { id: 17, name: "cfv", email: "abc123@example.com", mobile: "9123456780" },
//     { id: 18, name: "Hem", email: "abc123@example.com", mobile: "9123456780" },
//     { id: 19, name: "Hem", email: "ab@example.com", mobile: "9123456780" },
//     { id: 20, name: "Hem", email: "abc123@example.com", mobile: "9123456780" },
//     { id: 21, name: "Maitri1", email: "abc1@example.com", mobile: "9876543210" },
//     { id: 22, name: "Man2", email: "abc12@example.com", mobile: "1234567890" },
//     { id: 23, name: "Man", email: "abc12@example.com", mobile: "1234567890" },
//     { id: 24, name: "Man", email: "abc12@example.com", mobile: "1234567890" },
//     { id: 25, name: "abc", email: "ji@example.com", mobile: "1234567890" },
//     { id: 26, name: "Hem", email: "abc123@example.com", mobile: "9123456780" },
//     { id: 27, name: "Hem", email: "abc123@example.com", mobile: "9123456780" },
//     { id: 28, name: "Hem", email: "abc123@example.com", mobile: "9123456780" },
//     { id: 29, name: "bxem", email: "ajk@example.com", mobile: "9123456780" },
//     { id: 30, name: "Hem", email: "abc123@example.com", mobile: "9123456780" },
//   ]);

//   const [sortedTable, setSortedTable] = useState(tableData); // This will hold the sorted data
//   const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });

//   // Sort function that updates sortedTable
//   const handleSort = (column) => {
//     let direction = 'asc';
//     if (sortConfig.key === column && sortConfig.direction === 'asc') {
//       direction = 'desc';
//     }
//     setSortConfig({ key: column, direction });

//     const sortedData = [...tableData].sort((a, b) => {
//       if (a[column] < b[column]) return direction === 'asc' ? -1 : 1;
//       if (a[column] > b[column]) return direction === 'asc' ? 1 : -1;
//       return 0;
//     });

//     setSortedTable(sortedData); // Update the sortedTable state
//   };

//   const getSortIcon = (column) => {
//     if (sortConfig.key !== column) {
//       return <PiSortAscendingLight className="text-gray-500" />;
//     }
//     return sortConfig.direction === 'asc' ? <PiSortAscendingLight /> : <PiSortDescendingLight />;
//   };

//   useEffect(() => {
//     setSortedTable(tableData); // Initialize sortedTable with tableData when component mounts
//   }, [tableData]);


//   const [entriesPerPage, setEntriesPerPage] = useState(10); // Number of entries per page
//   const [currentPage, setCurrentPage] = useState(1); // Current page number

//   // Filtered data based on search query
//   const filteredData = sortedTable.filter((row) =>
//     row.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
//     row.email.toLowerCase().includes(searchQuery.toLowerCase())
//   );

//   // Paginated data based on the current page and entries per page
//   const startIndex = (currentPage - 1) * entriesPerPage;
//   const paginatedData = entriesPerPage === "All"
//     ? filteredData
//     : filteredData.slice(startIndex, startIndex + entriesPerPage);

//   const totalPages = Math.ceil(filteredData.length / entriesPerPage);

//   // Handle changes in the search query
//   const handleSearch = (e) => setSearchQuery(e.target.value);

//   // Handle changes in entries per page
//   const handleEntriesChange = (e) => {
//     setEntriesPerPage(e.target.value === "All" ? "All" : parseInt(e.target.value, 10));
//     setCurrentPage(1); // Reset to first page
//   };

//   // Pagination handlers
//   const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
//   const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
//   const handlePageChange = (page) => setCurrentPage(page);

//   const handleSubmit = (e) => {
//     e.preventDefault(); // Prevent the default form submission behavior

//     // Create new data object from the form
//     const newData = {
//       id: formData.id || tableData.length + 1, // Assign new ID if adding, else use the current ID for update
//       name: formData.name.trim(),
//       email: formData.email,
//       mobile: formData.mobile,

//     };

//     // If editing an existing entry, update that entry, else add a new one
//     if (formData.id) {
//       setTableData(tableData.map((row) =>
//         row.id === formData.id ? newData : row
//       ));
//     } else {
//       setTableData([...tableData, newData]);
//     }

//     // Reset form data for the next entry
//     setFormData({
//       id: null,
//       name: "",
//       email: "",
//       mobile: "",

//     });

//     // Show success message for a few seconds


//   };






//   // Handle deleting a staff entry
//   const handleDelete = (id) => {
//     setTableData(tableData.filter((row) => row.id !== id));
//   };

//   // Handle editing a staff entry
//   const handleEdit = (id) => {
//     // Find the staff entry by ID and populate the form with that data
//     const row = tableData.find((row) => row.id === id);
//     setFormData({
//       ...row,
//       name: row.name,
//       email: row.email,
//       mobile: row.mobile,


//     });
//   };

//   return (
//     <div className="min-h-screen bg-gray-100 p-4">
//       <div className="text-2xl font-bold p-4">
//         <span>Guest Customers</span>
//       </div>

//       <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4">
//         <div className="flex flex-col sm:flex-row sm:gap-11 sm:items-center mb-4 sm:mb-0 w-full sm:w-auto">
//           <input
//             type="text"
//             placeholder="Search guest customer..."
//             value={searchQuery}
//             onChange={handleSearch}
//             className="p-3 w-full sm:w-96 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
//           />
//           <div className="flex flex-wrap gap-2 sm:gap-4 mt-4 sm:mt-0">
//             <button className="bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold text-lg py-2 px-5 rounded-full shadow-lg hover:shadow-xl hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2">
//               <FaCopy className="text-2xl" />
//               Copy
//             </button>
//             <button className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-semibold text-lg py-2 px-5 rounded-full shadow-lg hover:shadow-xl hover:from-yellow-600 hover:to-yellow-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2">
//               <FaFileCsv className="text-2xl" />
//               CSV
//             </button>
//             <button className="bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold text-lg py-2 px-5 rounded-full shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2">
//               <FaFileExcel className="text-2xl" />
//               Excel
//             </button>
//             <button className="bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold text-lg py-2 px-5 rounded-full shadow-lg hover:shadow-xl hover:from-red-600 hover:to-red-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2">
//               <FaFilePdf className="text-2xl" />
//               PDF
//             </button>
//           </div>
//         </div>

//         <div className="flex items-center gap-2 mt-4 sm:mt-0">
//           <span>show</span>
//           <select
//             value={entriesPerPage}
//             onChange={handleEntriesChange}
//             className="p-3 m-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
//           >
//             <option value={10}>10</option>
//             <option value={25}>25</option>
//             <option value={50}>50</option>
//             <option value="All">All</option>
//           </select>
//           <span>entries</span>
//         </div>
//       </div>

//       <div className="overflow-x-auto">
//         <table className="min-w-full table-auto bg-white border border-gray-300 shadow-md rounded-lg">
//           <thead className="bg-blue-100 text-left">
//             <tr>
//               <th className="px-4 py-2 border-b">#</th>
//               <th
//                 className="px-4 py-2 border-b cursor-pointer"
//                 onClick={() => handleSort('name')}
//               >
//                 <div className="flex items-center gap-2">
//                   Customer name {getSortIcon('name')}
//                 </div>
//               </th>
//               <th
//                 className="px-4 py-2 border-b cursor-pointer"
//                 onClick={() => handleSort('email')}
//               >
//                 <div className="flex items-center gap-2">
//                   Email Address {getSortIcon('email')}
//                 </div>
//               </th>
//               <th
//                 className="px-4 py-2 border-b cursor-pointer"
//                 onClick={() => handleSort('mobile')}
//               >
//                 <div className="flex items-center gap-2">
//                   Mobile No. {getSortIcon('mobile')}
//                 </div>
//               </th>
//               <th className="px-4 py-2 border-b">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {sortedTable.length > 0 ? (
//               paginatedData.map((row, index) => (
//                 <tr key={row.id} className="hover:bg-gray-50">
//                   <td className="px-4 py-2 border-b">{index + 1 + startIndex}</td>
//                   <td className="px-4 py-2 border-b">{row.name}</td>
//                   <td className="px-4 py-2 border-b">{row.email}</td>
//                   <td className="px-4 py-2 border-b">{row.mobile}</td>
//                   <td className="px-4 py-2 border-b">
//                     <button
//                       onClick={() => handleEdit(row.id)}
//                       className="text-green-700 hover:text-green-900"
//                     >
//                       <BiSolidEdit className="text-3xl" />
//                     </button>
//                     <button
//                       onClick={() => handleDelete(row.id)}
//                       className="text-red-500 hover:text-red-600 ml-2"
//                     >
//                       <RiDeleteBin5Line className="text-3xl" />
//                     </button>
//                   </td>
//                 </tr>
//               ))
//             ) : (
//               <tr>
//                 <td
//                   colSpan="5"
//                   className="px-4 py-2 text-center text-gray-500 border-b"
//                 >
//                   No data found
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>

//       <div className="flex justify-between items-center mt-4">
//         <div className="text-sm text-gray-600">
//           <span>Showing </span><span>1</span><span> to </span><span>5</span><span> of </span><span>2</span><span> entries</span>
//         </div>

//         {/* Pagination Controls */}
//         {entriesPerPage !== "All" && (
//           <div className="flex items-center">
//             <button
//               onClick={handlePrevPage}
//               disabled={currentPage === 1}
//               className="px-4 py-2 bg-gray-300 rounded-l hover:bg-gray-400 disabled:opacity-50"
//             >
//               Previous
//             </button>
//             {[...Array(totalPages).keys()].map((page) => (
//               <button
//                 key={page + 1}
//                 onClick={() => handlePageChange(page + 1)}
//                 className={`px-4 py-2 border ${currentPage === page + 1
//                   ? "bg-blue-500 text-white"
//                   : "bg-gray-200 hover:bg-gray-300"
//                   }`}
//               >
//                 {page + 1}
//               </button>
//             ))}
//             <button
//               onClick={handleNextPage}
//               disabled={currentPage === totalPages}
//               className="px-4 py-2 bg-gray-300 rounded-r hover:bg-gray-400 disabled:opacity-50"
//             >
//               Next
//             </button>
//           </div>
//         )}
//       </div>

//     </div>

//   );
// }

// export default Guest;


